/**
 * Media Registrations
 * 
 * This file registers all available media types with their renderers and formatters.
 */
import { zTextFormat, zDateFormat, zNumberFormat, zColorFormat, zImageFormat, TextFormat, DateFormat, NumberFormat, ColorFormat, ImageFormat, MediaType } from '../../../core/template.schemas';
import { MediaRegistry, NumberFormatter, DateFormatter } from './mediaRegistry';

/**
 * Default formats for each media type - now using proper TypeScript types
 */
export const DEFAULT_FORMATS = {
  text: {} as TextFormat,                           // Empty object for default text format
  date: { pattern: 'DD.MM.YYYY' } as DateFormat,    // MM.DD.YYYY Format
  int: {} as NumberFormat,                          // Empty object for default number format
  float: { decimals: 2 } as NumberFormat,          // Zwei Nachkommastellen
  color: {} as ColorFormat,                         // Empty object for default color format
  image: { fit: 'contain' } as ImageFormat            // Cover-Modus für Bilder
} as const;

/**
 * Register all standard media types
 */
export function registerMediaTypes(registry: MediaRegistry): void {
  // Register text media type
  registry.register('text', {
    formatSchema: zTextFormat as any,
    applyFormat: (raw: any, fmt?: any) => {
      const text = String(raw ?? '');
      
      // Use default format if no format specified
      const format = fmt || DEFAULT_FORMATS.text;
      
      // Handle format object with 'type' property
      if (format?.type) {
        switch (format.type) {
          case 'uppercase':
            return text.toUpperCase();
          case 'lowercase':
            return text.toLowerCase();
          case 'title':
            return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
          case 'default':
          default:
            return text; // Unverändert
        }
      }
      
      // Handle proper TextFormat structure
      if (format?.kind === 'uppercase') return text.toUpperCase();
      if (format?.kind === 'lowercase') return text.toLowerCase();
      if (format?.kind === 'title') return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
      
      return text;
    },
    editorField: 'TextInput',
    generatorField: 'TextInput',
    validate: (value: any) => typeof value === 'string',
    transform: (input: any) => String(input ?? ''),
    icon: 'type',
    color: 'blue',
    displayName: 'Text'
  });

  // Register image media type
  registry.register('image', {
    formatSchema: zImageFormat as any,
    applyFormat: (raw: any, fmt?: any) => {
      // If raw is already an object (e.g. from manual overrides), pass it through
      if (raw && typeof raw === 'object') {
        return raw;
      }

      const imageUrl = String(raw ?? '');
      
      // Use default format if no format specified
      const format = fmt || DEFAULT_FORMATS.image;
      
      // Handle format object with 'type' property for image fit
      if (format?.type) {
        // For images, the format type affects how the image is displayed
        // but the resolved value is still the URL string
        return imageUrl;
      }
      
      return imageUrl;
    },
    editorField: 'ImagePicker',
    generatorField: 'ImagePicker',
    validate: (value: any) => typeof value === 'string' && value.length > 0,
    transform: (input: any) => String(input ?? ''),
    icon: 'image',
    color: 'green',
    displayName: 'Bild'
  });

  // Register date media type
  registry.register('date', {
    formatSchema: zDateFormat as any,
    applyFormat: (raw: any, fmt?: any) => {
      // Use default format if no format specified
      const format = fmt || DEFAULT_FORMATS.date;
      
      // Directly use the pattern from the format object
      return DateFormatter.format(raw, format?.pattern || 'DD.MM.YYYY');
    },
    editorField: 'DatePicker',
    generatorField: 'DatePicker',
    validate: (value: any) => {
      if (!value) return false;
      const date = new Date(value);
      return !isNaN(date.getTime());
    },
    transform: (input: any) => {
      if (input instanceof Date) return input.toISOString();
      if (typeof input === 'string' || typeof input === 'number') {
        const date = new Date(input);
        return isNaN(date.getTime()) ? null : date.toISOString();
      }
      return null;
    },
    icon: 'calendar',
    color: 'purple',
    displayName: 'Datum'
  });

  // Register color media type
  registry.register('color', {
    formatSchema: zColorFormat as any,
    applyFormat: (raw: any, fmt?: any) => {
      // Color always returns hex string regardless of format
      return String(raw ?? '#000000');
    },
    editorField: 'ColorPicker',
    generatorField: 'ColorPicker',
    validate: (value: any) => {
      if (typeof value !== 'string') return false;
      return /^#[0-9a-fA-F]{6}$/.test(value);
    },
    transform: (input: any) => {
      const str = String(input ?? '');
      if (/^#[0-9a-fA-F]{6}$/.test(str)) return str;
      if (/^[0-9a-fA-F]{6}$/.test(str)) return `#${str}`;
      return '#000000';
    },
    icon: 'palette',
    color: 'red',
    displayName: 'Farbe'
  });

  // Register integer media type
  registry.register('int', {
    formatSchema: zNumberFormat as any,
    applyFormat: (raw: any, fmt?: any) => {
      // Use default format if no format specified
      const format = fmt || DEFAULT_FORMATS.int;
      
      // Handle format object with 'type' property
      if (format?.type) {
        switch (format.type) {
          case 'thousands':
            return NumberFormatter.format(raw, { decimals: 0, thousands: true });
          case 'default':
          default:
            return NumberFormatter.format(raw, { decimals: 0 }); // Unverändert
        }
      }
      
      // Handle proper NumberFormat structure
      return NumberFormatter.format(raw, { ...format, decimals: 0 });
    },
    editorField: 'NumberInput',
    generatorField: 'NumberInput',
    validate: (value: any) => Number.isInteger(Number(value)),
    transform: (input: any) => {
      const num = Number(input);
      return isNaN(num) ? 0 : Math.floor(num);
    },
    icon: 'hash',
    color: 'indigo',
    displayName: 'Ganzzahl'
  });

  // Register float media type
  registry.register('float', {
    formatSchema: zNumberFormat as any,
    applyFormat: (raw: any, fmt?: any) => {
      // Use default format if no format specified
      const format = fmt || DEFAULT_FORMATS.float;
      
      // Handle format object with 'type' property
      if (format?.type) {
        switch (format.type) {
          case 'none':
            return NumberFormatter.format(raw, { preserve: true, thousands: false });
          case 'stripZeros':
            return NumberFormatter.format(raw, { decimals: 2, thousands: false, trimZeros: true });
          case 'decimal2':
            return NumberFormatter.format(raw, { decimals: 2, thousands: false });
          case 'thousands_trim':
            return NumberFormatter.format(raw, { decimals: 2, thousands: true, trimZeros: true });
          case 'thousands_2':
            return NumberFormatter.format(raw, { decimals: 2, thousands: true });
          case 'integer':
            return NumberFormatter.format(raw, { decimals: 0, thousands: !!format.thousands, roundMode: format.roundMode || 'round' });
          case 'default':
          default:
            return NumberFormatter.format(raw, { decimals: 2, thousands: false });
        }
      }
      
      // Handle proper NumberFormat structure
      return NumberFormatter.format(raw, { decimals: 2, ...format });
    },
    editorField: 'NumberInput',
    generatorField: 'NumberInput',
    validate: (value: any) => !isNaN(Number(value)),
    transform: (input: any) => {
      const num = Number(input);
      return isNaN(num) ? 0 : num;
    },
    icon: 'calculator',
    color: 'teal',
    displayName: 'Kommazahl'
  });
}

/**
 * Get default format for a media type
 * @param mediaType - The media type to get default format for
 * @returns Default format object for the media type
 */
export function getDefaultFormat(mediaType: MediaType): TextFormat | DateFormat | NumberFormat | ColorFormat | ImageFormat {
  return DEFAULT_FORMATS[mediaType];
}